import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, Users, Star, Play, ArrowLeft, Radio } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TheaterSeats from './TheaterSeats';
import { API_CONFIG } from '../config/apiConfig';

const WatchPartyTheater = ({ onClose, currentUser }) => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [currentEventIndex, setCurrentEventIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [participants, setParticipants] = useState([]);

    const [remoteStream, setRemoteStream] = useState(null);
    const [isReceivingStream, setIsReceivingStream] = useState(false);
    const remoteVideoRef = useRef(null);
    const peerConnectionRef = useRef(null);

    const getAdminToken = () => localStorage.getItem('cinestream_token');

    useEffect(() => {
        fetchEvents();
        const interval = setInterval(fetchEvents, 10000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (events.length > 0 && currentUser) {
            const currentEvent = events[currentEventIndex];
            updatePresence(currentEvent.id);

            const heartbeat = setInterval(() => {
                updatePresence(currentEvent.id);
            }, 10000);

            const participantsRefresh = setInterval(() => {
                fetchParticipants(currentEvent.id);
            }, 5000);

            return () => {
                clearInterval(heartbeat);
                clearInterval(participantsRefresh);
            };
        }
    }, [events, currentEventIndex, currentUser]);

    const fetchEvents = async () => {
        try {
            const token = getAdminToken();
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/watch-party/events?filter=upcoming`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token || ''
                }
            });
            const data = await response.json();
            if (data.success && data.events) {
                const sortedEvents = data.events.sort((a, b) =>
                    new Date(a.scheduledDateTime) - new Date(b.scheduledDateTime)
                );
                setEvents(sortedEvents);
                if (sortedEvents.length > 0) fetchParticipants(sortedEvents[0].id);
            }
        } catch (error) {
            console.error('Erreur chargement événements:', error);
        }
        setLoading(false);
    };

    const updatePresence = async (eventId) => {
        if (!currentUser || !currentUser.username) return;
        try {
            const token = getAdminToken();
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/watch-party/${eventId}/participants/join`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token || ''
                },
                body: JSON.stringify({ username: currentUser.username }),
            });
            const data = await response.json();
            if (data.success) setParticipants(data.participants || []);
        } catch (error) {
            console.error('Erreur update présence:', error);
        }
    };

    const fetchParticipants = async (eventId) => {
        try {
            const token = getAdminToken();
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/watch-party/${eventId}/participants`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token || ''
                }
            });
            const data = await response.json();
            if (data.success) setParticipants(data.participants || []);
        } catch (error) {
            console.error('Erreur chargement participants:', error);
        }
    };

    const getCountdown = (event) => {
        if (event.status === 'live') return { days: 0, hours: 0, minutes: 0, expired: true, isLive: true };
        const now = new Date();
        const diff = new Date(event.scheduledDateTime) - now;
        if (diff < 0) return { days: 0, hours: 0, minutes: 0, expired: true, isLive: false };
        return {
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
            expired: false,
            isLive: false,
        };
    };

    const formatDate = (dateTimeStr) =>
        new Intl.DateTimeFormat('fr-FR', {
            weekday: 'long', day: 'numeric', month: 'long',
            hour: '2-digit', minute: '2-digit',
        }).format(new Date(dateTimeStr));

    const handlePrevEvent = () => {
        const newIndex = currentEventIndex > 0 ? currentEventIndex - 1 : events.length - 1;
        setCurrentEventIndex(newIndex);
        fetchParticipants(events[newIndex].id);
    };

    const handleNextEvent = () => {
        const newIndex = currentEventIndex < events.length - 1 ? currentEventIndex + 1 : 0;
        setCurrentEventIndex(newIndex);
        fetchParticipants(events[newIndex].id);
    };

    const navBtnStyle = {
        background: 'rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 'var(--r-lg)',
        color: '#fff',
        padding: '12px 20px',
        fontSize: '1.25rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'background 150ms ease',
    };

    if (loading) {
        return (
            <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ width: 56, height: 56, borderRadius: '50%', border: '3px solid var(--glass-border)', borderTopColor: 'var(--accent)', animation: 'spinSlow 0.8s linear infinite', margin: '0 auto 16px' }} />
                    <p style={{ color: '#fff', fontSize: '1.125rem' }}>Chargement de la salle...</p>
                </div>
            </div>
        );
    }

    if (events.length === 0) {
        return (
            <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                <div style={{ textAlign: 'center', maxWidth: 440 }}>
                    <div style={{ fontSize: 64, marginBottom: 16 }}>🎬</div>
                    <h2 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1.5rem', marginBottom: 8 }}>Aucune séance programmée</h2>
                    <p style={{ color: 'var(--text-4)', marginBottom: 24 }}>Revenez bientôt pour découvrir les prochaines Watch Party !</p>
                    <button onClick={onClose} className="btn-primary" style={{ padding: '12px 28px' }}>
                        Retour à l'accueil
                    </button>
                </div>
            </div>
        );
    }

    const currentEvent = events[currentEventIndex];
    const countdown = getCountdown(currentEvent);
    const isLiveStream = currentEvent.eventType === 'live_stream';

    return (
        <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 50, overflowY: 'auto' }}>
            {/* Background gradient */}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(232,183,90,0.15) 0%, #000 60%)' }} />

            {/* Spotlight (desktop) */}
            <div className="hidden md:block" style={{
                position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                width: '40vw', height: '40vw',
                background: 'rgba(232,183,90,0.08)',
                filter: 'blur(80px)', borderRadius: '50%',
                animation: 'glowPulse 3s ease-in-out infinite',
            }} />

            {/* Header */}
            <div style={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px' }}>
                <button
                    onClick={onClose}
                    className="btn-ghost"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px' }}
                >
                    <ArrowLeft style={{ width: 18, height: 18 }} />
                    <span style={{ display: 'none' }} className="sm:inline">Retour</span>
                </button>

                {events.length > 1 && (
                    <div style={{
                        background: 'var(--glass)', border: '1px solid var(--glass-border)',
                        borderRadius: 'var(--r-lg)', padding: '8px 16px',
                        color: 'var(--text-1)', fontWeight: 600, fontSize: '0.875rem',
                    }}>
                        {currentEventIndex + 1} / {events.length}
                    </div>
                )}
            </div>

            <div style={{ position: 'relative', padding: '16px 20px 40px', maxWidth: 1200, margin: '0 auto' }}>
                {/* Cinema screen frame */}
                <div style={{
                    background: 'linear-gradient(135deg, #1A1A2E, #0D0D1A)',
                    padding: '8px',
                    borderRadius: 'var(--r-2xl)',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.8)',
                    border: '6px solid rgba(255,255,255,0.06)',
                }}>
                    {/* Screen */}
                    <div style={{ position: 'relative', aspectRatio: '16/9', background: '#000', borderRadius: 'var(--r-xl)', overflow: 'hidden' }}>
                        {/* Backdrop */}
                        <img
                            src={currentEvent.movieBackdrop || currentEvent.moviePoster}
                            alt={currentEvent.movieTitle}
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4, filter: 'blur(4px)' }}
                        />

                        {/* Gradient overlay */}
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, #000 0%, rgba(0,0,0,0.7) 50%, transparent 100%)' }} />

                        {/* LIVE STREAM badge */}
                        {isLiveStream && (
                            <div style={{
                                position: 'absolute', top: 16, left: 16,
                                background: '#EF4444', color: '#fff',
                                padding: '8px 16px', borderRadius: 'var(--r-lg)',
                                fontWeight: 700, fontSize: '0.875rem',
                                display: 'flex', alignItems: 'center', gap: 8,
                                animation: 'glowPulse 2s ease-in-out infinite',
                                zIndex: 10,
                            }}>
                                <Radio style={{ width: 18, height: 18 }} />
                                LIVE STREAM
                            </div>
                        )}

                        {/* Content */}
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', padding: '24px 32px' }}>
                            <div style={{ display: 'flex', flexDirection: 'row', gap: 24, width: '100%', flexWrap: 'wrap' }}>
                                {/* Poster */}
                                <div style={{ flexShrink: 0 }}>
                                    <img
                                        src={currentEvent.moviePoster}
                                        alt={currentEvent.movieTitle}
                                        style={{
                                            width: 120, height: 180,
                                            objectFit: 'cover',
                                            borderRadius: 'var(--r-xl)',
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                                            border: '2px solid rgba(255,255,255,0.1)',
                                        }}
                                        className="sm:w-40 sm:h-60"
                                    />
                                </div>

                                {/* Info */}
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                                    <h1 style={{
                                        color: '#fff', fontWeight: 800,
                                        fontSize: 'clamp(1.5rem, 4vw, 3rem)',
                                        lineHeight: 1.1, marginBottom: 12,
                                        textShadow: '0 2px 24px rgba(0,0,0,0.5)',
                                    }}>
                                        {currentEvent.movieTitle}
                                    </h1>

                                    {/* Meta */}
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', marginBottom: 10 }}>
                                        {currentEvent.movieYear && (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <Calendar style={{ width: 14, height: 14 }} />
                                                {currentEvent.movieYear}
                                            </span>
                                        )}
                                        {currentEvent.movieDuration && (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <Clock style={{ width: 14, height: 14 }} />
                                                {currentEvent.movieDuration}
                                            </span>
                                        )}
                                        {currentEvent.movieRating && (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#FCD34D' }}>
                                                <Star style={{ width: 14, height: 14, fill: '#FCD34D' }} />
                                                {currentEvent.movieRating}/10
                                            </span>
                                        )}
                                    </div>

                                    {/* Genres */}
                                    {currentEvent.movieGenres?.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                                            {currentEvent.movieGenres.slice(0, 3).map(genre => (
                                                <span
                                                    key={genre}
                                                    style={{
                                                        padding: '3px 12px',
                                                        background: 'rgba(232,183,90,0.25)',
                                                        border: '1px solid rgba(232,183,90,0.35)',
                                                        borderRadius: 999,
                                                        color: 'var(--accent-bright)',
                                                        fontSize: '0.75rem',
                                                        backdropFilter: 'blur(8px)',
                                                    }}
                                                >
                                                    {genre}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    {/* Synopsis desktop */}
                                    {currentEvent.movieOverview && (
                                        <p className="hidden md:block" style={{
                                            color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem',
                                            lineHeight: 1.6, maxWidth: 600,
                                            display: '-webkit-box', WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                        }}>
                                            {currentEvent.movieOverview}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Synopsis mobile */}
                {currentEvent.movieOverview && (
                    <div className="md:hidden" style={{
                        marginTop: 16,
                        background: 'var(--glass)',
                        border: '1px solid var(--glass-border)',
                        borderRadius: 'var(--r-xl)',
                        padding: 16,
                    }}>
                        <p style={{ color: 'var(--text-3)', fontSize: '0.875rem', lineHeight: 1.6 }}>
                            {currentEvent.movieOverview}
                        </p>
                    </div>
                )}

                {/* Session info */}
                <div style={{
                    marginTop: 24,
                    background: 'linear-gradient(135deg, rgba(232,183,90,0.12), rgba(156,116,47,0.08))',
                    border: '1px solid rgba(232,183,90,0.25)',
                    backdropFilter: 'blur(12px)',
                    borderRadius: 'var(--r-2xl)',
                    padding: '24px 28px',
                }}>
                    <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: 24 }}>
                        {/* Date */}
                        <div>
                            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--accent-bright)', fontWeight: 700, fontSize: '0.9375rem', marginBottom: 10 }}>
                                <Calendar style={{ width: 18, height: 18 }} />
                                Séance programmée
                            </h3>
                            <p style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1.25rem', marginBottom: 8 }}>
                                {formatDate(currentEvent.scheduledDateTime)}
                            </p>
                            {currentEvent.description && (
                                <p style={{ color: 'var(--text-3)', fontSize: '0.875rem', fontStyle: 'italic', marginTop: 8 }}>
                                    💬 {currentEvent.description}
                                </p>
                            )}
                        </div>

                        {/* Countdown */}
                        <div>
                            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--accent-bright)', fontWeight: 700, fontSize: '0.9375rem', marginBottom: 10 }}>
                                <Clock style={{ width: 18, height: 18 }} />
                                Début dans
                            </h3>
                            {!countdown.expired ? (
                                <div style={{ display: 'flex', gap: 12 }}>
                                    {countdown.days > 0 && (
                                        <div style={{ textAlign: 'center', flex: 1 }}>
                                            <div style={{
                                                fontSize: '2rem', fontWeight: 800, color: 'var(--text-1)',
                                                background: 'var(--glass)', border: '1px solid var(--glass-border)',
                                                borderRadius: 'var(--r-lg)', padding: '10px 8px',
                                            }}>
                                                {countdown.days}
                                            </div>
                                            <div style={{ color: 'var(--text-4)', fontSize: '0.75rem', marginTop: 4 }}>jours</div>
                                        </div>
                                    )}
                                    <div style={{ textAlign: 'center', flex: 1 }}>
                                        <div style={{
                                            fontSize: '2rem', fontWeight: 800, color: 'var(--text-1)',
                                            background: 'var(--glass)', border: '1px solid var(--glass-border)',
                                            borderRadius: 'var(--r-lg)', padding: '10px 8px',
                                        }}>
                                            {countdown.hours}
                                        </div>
                                        <div style={{ color: 'var(--text-4)', fontSize: '0.75rem', marginTop: 4 }}>heures</div>
                                    </div>
                                    <div style={{ textAlign: 'center', flex: 1 }}>
                                        <div style={{
                                            fontSize: '2rem', fontWeight: 800, color: 'var(--text-1)',
                                            background: 'var(--glass)', border: '1px solid var(--glass-border)',
                                            borderRadius: 'var(--r-lg)', padding: '10px 8px',
                                        }}>
                                            {countdown.minutes}
                                        </div>
                                        <div style={{ color: 'var(--text-4)', fontSize: '0.75rem', marginTop: 4 }}>min</div>
                                    </div>
                                </div>
                            ) : (
                                <p style={{
                                    fontSize: '1.125rem', fontWeight: 700,
                                    color: countdown.isLive ? '#4ADE80' : '#22C55E',
                                    animation: countdown.isLive ? 'glowPulse 2s ease-in-out infinite' : 'none',
                                }}>
                                    {countdown.isLive ? '🔴 Séance en cours !' : "C'est maintenant !"}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
                        <button
                            onClick={() => {
                                if (currentEvent.status === 'live') {
                                    navigate(`/watch-party/${currentEvent.id}`);
                                } else {
                                    alert("La séance n'a pas encore commencé. Revenez à l'heure prévue !");
                                }
                            }}
                            style={{
                                flex: 1, minWidth: 200,
                                padding: '14px 24px',
                                borderRadius: 'var(--r-xl)',
                                fontWeight: 700, fontSize: '1rem',
                                border: 'none', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                background: currentEvent.status === 'live'
                                    ? 'linear-gradient(135deg, #16A34A, #15803D)'
                                    : 'linear-gradient(135deg, var(--accent), var(--accent-deep, #9C742F))',
                                color: '#fff',
                                boxShadow: currentEvent.status === 'live'
                                    ? '0 4px 24px rgba(22,163,74,0.4)'
                                    : '0 4px 24px rgba(232,183,90,0.35)',
                                animation: currentEvent.status === 'live' ? 'glowPulse 2s ease-in-out infinite' : 'none',
                            }}
                        >
                            {currentEvent.status === 'live' ? (
                                <>
                                    <Play style={{ width: 22, height: 22, fill: '#fff' }} />
                                    <span>🔴 Rejoindre MAINTENANT</span>
                                </>
                            ) : (
                                <>
                                    <Users style={{ width: 20, height: 20 }} />
                                    <span>Rejoindre la Watch Party</span>
                                </>
                            )}
                        </button>

                        {events.length > 1 && (
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    onClick={handlePrevEvent}
                                    style={navBtnStyle}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                >
                                    ←
                                </button>
                                <button
                                    onClick={handleNextEvent}
                                    style={navBtnStyle}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                >
                                    →
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Theater seats */}
                <div style={{ marginTop: 32 }}>
                    <TheaterSeats participants={participants} />
                </div>
            </div>
        </div>
    );
};

export default WatchPartyTheater;
